import React from 'react';
import I18n from 'i18n-js';
import ActionBar from '@/components/shared/NewLessonOverview/Cards/ActionBar';
import ActionBarLink from '@/components/shared/NewLessonOverview/Cards/ActionBar/ActionBarButtons/ActionBarLink';
import Card, { CardBody } from '@/components/shared/NewLessonOverview/Cards/Card';
import Edit from '@/components/shared/NewLessonOverview/Cards/ActionBar/ActionBarButtons/SlideShowButtons/Edit';
import useLessonOverviewContext from '@/components/shared/NewLessonOverview/useLessonOverviewContext';
import { ICON_MAP } from '@/components/shared/NewLessonOverview/LessonOptions/utils';
import { dateTimeToUserTimeZone } from '@/utils/functions';
import { lessonSlideShowInitialValues } from '@/components/shared/NewLessonOverview/Cards/utils';
import deleteIcon from 'images/icons/delete-lined.svg';
import printIcon from 'images/icons/printK5.svg';

interface SlideShowCardProps {
  iconColor: string;
  isTeacherSlideShow?: boolean;
  slideShow: any;
  truncateTextTooltip?: boolean;
}

const SlideShowCard = ({
  iconColor,
  isTeacherSlideShow,
  slideShow,
  truncateTextTooltip = false,
}: SlideShowCardProps) => {
  const isStaffer = useLessonOverviewContext(state => state.isStaffer);
  const ariaLabel = `${I18n.t('print')} ${slideShow?.title}`;

  const time = isTeacherSlideShow ? undefined : slideShow.lesson?.class_time;
  const page = isTeacherSlideShow ? undefined : slideShow.page;
  const slideShowUpdateTime = dateTimeToUserTimeZone(slideShow.updatedAt, 'l');
  const slideShowUpdateLabel = I18n.t('last_updated_on', { time: slideShowUpdateTime });

  const topLabel = isTeacherSlideShow ? slideShowUpdateLabel : undefined;
  const indicatorMessage = isTeacherSlideShow ? I18n.t('teacher_slideshow_tooltip') : undefined;
  const initialValues = isStaffer ? lessonSlideShowInitialValues(slideShow) : null;

  const thumbnail = (slideShow.thumbnailUrl !== '/missing.png') ? slideShow.thumbnailUrl : '/placeholder.png';

  return (
    <Card type={slideShow.activityType}>
      <CardBody
        openInNewWindow
        ariaLabel={slideShow.title}
        iconColor={iconColor}
        iconSrc={ICON_MAP[slideShow.activityType]}
        linkHref={slideShow.links?.unfilteredPresentPath}
        linkLabel={slideShow.title}
        page={page}
        showThumbnail={!isTeacherSlideShow}
        thumbnail={thumbnail}
        time={time}
        tooltipIndicator={indicatorMessage}
        topLabel={topLabel}
        truncateTextTooltip={truncateTextTooltip}
      />
      <ActionBar
        href={slideShow.links?.unfilteredPresentPath}
        iconType="play"
        initialValues={initialValues}
        isAssigned={slideShow.isAssigned}
      >
        {isStaffer && (
          <>
            <Edit
              editExistingSlideShow={isTeacherSlideShow}
              slideShow={slideShow}
            />
            {slideShow.activityType !== 'video_activity' && slideShow.links?.printUrl && (
              <ActionBarLink
                href={slideShow.links?.printUrl}
                icon={printIcon}
                label={ariaLabel}
                tag="print"
              />
            )}
            {isTeacherSlideShow && !!slideShow.links?.deletePath && (
              <ActionBarLink
                dataConfirm="Are you sure you want to delete this lesson guide?"
                dataMethod="delete"
                href={slideShow.links.deletePath}
                icon={deleteIcon}
                tag="delete"
                useNewTab={false}
              />
            )}
          </>
        )}
      </ActionBar>
    </Card>
  );
};

export default SlideShowCard;
